import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import {
  CommunicatorTab,
  encode,
  ICommunicatorDeepLinkState,
  normalize,
  parse,
} from 'core/utils/communicatorDeepLink';

/**
 * Thin React wrapper over `core/utils/communicatorDeepLink`: reads the
 * communicator view state from `location.search` and commits transitions via
 * `history.replace` (tab/selection switches should not pollute the back
 * stack — same convention as the dashboard and the group card).
 */
export const useCommunicatorDeepLink = () => {
  const history = useHistory();
  const location = useLocation();

  const state = useMemo(() => parse(location.search), [location.search]);

  const commit = useCallback(
    (next: ICommunicatorDeepLinkState) => {
      history.replace({
        ...location,
        search: encode(normalize(next)).toString(),
      });
    },
    [history, location],
  );

  /** Select a group ('' deselects). Keeps tab and search, resets context. */
  const setGroup = useCallback(
    (groupId: string) =>
      commit({
        ...state,
        group: groupId,
        student: '',
        teacher: '',
        ctx: '',
        conv: '',
      }),
    [commit, state],
  );

  /** Select a student of the current group ('' returns to the whole group). */
  const setStudent = useCallback(
    (studentId: string) =>
      commit({ ...state, student: studentId, teacher: '', ctx: '', conv: '' }),
    [commit, state],
  );

  /** Выбрать преподавателя группы (ТЗ 0032); '' — назад ко «всей группе». */
  const setTeacher = useCallback(
    (teacherId: string) =>
      commit({ ...state, teacher: teacherId, student: '', ctx: '', conv: '' }),
    [commit, state],
  );

  /** Select the pinned "group channels" context. */
  const setChannelsCtx = useCallback(
    () =>
      commit({ ...state, student: '', teacher: '', ctx: 'channels', conv: '' }),
    [commit, state],
  );

  const setTab = useCallback(
    (tab: CommunicatorTab) => commit({ ...state, tab, conv: '' }),
    [commit, state],
  );

  /** Preselect a conversation inside the current context (optionally + tab). */
  const openConversation = useCallback(
    (conv: string, patch?: Partial<ICommunicatorDeepLinkState>) =>
      commit({ ...state, ...patch, conv }),
    [commit, state],
  );

  const setQuery = useCallback((query: string) => commit({ ...state, query }), [
    commit,
    state,
  ]);

  return {
    state,
    setGroup,
    setStudent,
    setTeacher,
    setChannelsCtx,
    setTab,
    openConversation,
    setQuery,
  };
};
